import React from "react";
import clsx from "clsx";
import { theme } from "antd";

const SHARE_PRICE_DEEP_DIVE_ICON = "assets/slides/slideIcons/chartRelativeBenchmark.png";
const CAPITALIZATION_DEEP_DIVE_ICON = "assets/slides/slideIcons/capitalizationDeepDive.png";

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
    <polyline
      points="2.5,7 6,10.5 11.5,3.5"
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const CHART_PRESENTATION_TYPE_OPTIONS = [
  {
    id: "share-price-deep-dive",
    label: "Share Price Deep Dive",
    description:
      "Analyze the Primary Company's share price flexing time periods and relative benchmarks to highlight different narratives within the broader equity story.",
    icon: SHARE_PRICE_DEEP_DIVE_ICON,
    isAvailable: true,
    unavailableIndex: null,
  },
  {
    id: "capitalization-deep-dive",
    label: "Capitalization Deep Dive",
    description:
      "Review the Primary Company's current and historical capitalization, examine underlying instruments, compare to peers, and highlight changes over time.",
    icon: CAPITALIZATION_DEEP_DIVE_ICON,
    isAvailable: false,
    unavailableIndex: null,
  },
] as const;

export type ChartPresentationTypeId = (typeof CHART_PRESENTATION_TYPE_OPTIONS)[number]["id"];

const ChartIcon = ({ icon, alt }: { icon: string; alt: string }) => (
  <img
    src={icon}
    alt={alt}
    width={56}
    height={56}
    className="h-24 w-24 mb-2 shrink-0 object-contain"
    loading="lazy"
  />
);

type ChartTypeOptionCardProps = {
  label: string;
  description: string;
  icon: string;
  selected: boolean;
  onSelect: () => void;
  isAvailable: boolean;
  unavailableIndex: number | null;
};

function ChartTypeOptionCard({
  label,
  description,
  icon,
  selected,
  onSelect,
  isAvailable,
  unavailableIndex,
}: ChartTypeOptionCardProps) {
  const { token } = theme.useToken();
  const buttonToken: any = token?.Button;
  const primaryColor = buttonToken?.colorPrimary;

  const selectedAvailable = selected && isAvailable;

  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      aria-disabled={!isAvailable}
      disabled={!isAvailable}
      onClick={() => {
        if (isAvailable) onSelect();
      }}
      className={clsx(
        "relative flex min-w-0 flex-1 flex-col items-start gap-3 rounded-xl border-[1.5px] px-4 pb-4 pt-5 text-left outline-none transition-[border-color,background-color] duration-150 ease-out",
        selectedAvailable ? "bg-green-50" : "border-gray-200 bg-white",
        isAvailable && !selected && "cursor-pointer hover:border-gray-300",
        isAvailable && selected && "cursor-pointer",
        !isAvailable && "cursor-not-allowed opacity-90"
      )}
      style={selectedAvailable ? { borderColor: primaryColor } : undefined}
    >
      <div className="absolute right-3.5 top-3.5">
        {selected && isAvailable ? (
          <div
            className="flex h-6 w-6 items-center justify-center rounded-full"
            style={{ backgroundColor: primaryColor }}
          >
            <CheckIcon />
          </div>
        ) : !isAvailable && unavailableIndex !== null ? (
          <div className="flex h-6 w-6 items-center justify-center rounded-full border-2 border-red-500 bg-white text-xs font-bold text-red-500">
            {unavailableIndex}
          </div>
        ) : (
          <div className="h-6 w-6 rounded-full border-[1.5px] border-gray-300 bg-white" />
        )}
      </div>

      <div className="flex w-full items-start gap-5">
        <div className="relative w-30 flex items-center justify-center">
          {isAvailable ? (
            selected ? (
              <span
                className="absolute -top-2 left-0 w-30 inline-block rounded-md px-[9px] py-0.5 text-center text-[11px] font-semibold leading-[18px] tracking-wide text-white"
                style={{ backgroundColor: primaryColor }}
              >
                Selected
              </span>
            ) : null
          ) : (
            <span className="absolute -top-2 left-0 w-30 text-center inline-block rounded-md bg-red-100 px-[9px] py-0.5 text-[11px] font-semibold leading-[18px] tracking-wide text-red-600">
              Not Yet Available
            </span>
          )}
          <ChartIcon icon={icon} alt={label} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="mb-1.5 text-[15px] font-semibold leading-snug text-gray-900">{label}</div>
          <div className="text-[13px] leading-normal text-gray-500">{description}</div>
        </div>
      </div>
    </button>
  );
}

type PresentationChartTypeRadioListProps = {
  value: string | null;
  onChange: (id: ChartPresentationTypeId) => void;
};

export default function PresentationChartTypeRadioList({
  value,
  onChange,
}: PresentationChartTypeRadioListProps) {
  return (
    <div
      className="mt-3 flex flex-row items-stretch gap-4"
      role="radiogroup"
      aria-label="Presentation chart type"
    >
      {CHART_PRESENTATION_TYPE_OPTIONS.map((opt) => {
        const selected = value === opt.id;
        return (
          <ChartTypeOptionCard
            key={opt.id}
            label={opt.label}
            description={opt.description}
            icon={opt.icon}
            selected={selected}
            onSelect={() => onChange(opt.id)}
            isAvailable={opt.isAvailable}
            unavailableIndex={opt.unavailableIndex}
          />
        );
      })}
    </div>
  );
}
